import React from "react";
import { Button, Empty, Tooltip as AntTooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

export function Shimmer({ className = "", style }) {
  return <div className={`ga-shimmer ${className}`} style={style} />;
}

export function ChartSkeleton({ type, height = 280 }) {
  const heightVar = { "--h": `${height}px` };
  if (type === "bar") {
    const heights = [55, 78, 42, 92, 68, 50, 85, 62, 75, 48];
    return (
      <div className="h-[var(--h)] flex items-end gap-2 py-4 px-1" style={heightVar}>
        {heights.map((h, i) => (
          <Shimmer key={i} className="flex-1 h-[var(--bar-h)] rounded-[4px_4px_0_0]" style={{ "--bar-h": `${h}%` }} />
        ))}
      </div>
    );
  }
  if (type === "line") {
    return (
      <div className="h-[var(--h)] w-full relative py-4" style={heightVar}>
        <Shimmer className="absolute top-[28%] left-0 right-0 h-1 rounded-sm" />
        <Shimmer className="absolute top-[52%] left-0 right-0 h-1 rounded-sm opacity-70" />
        <Shimmer className="absolute top-[72%] left-0 right-0 h-1 rounded-sm opacity-[0.45]" />
      </div>
    );
  }
  if (type === "area") {
    return (
      <div className="h-[var(--h)] py-4 flex items-end" style={heightVar}>
        <Shimmer className="w-full h-[75%] rounded-[8px_8px_4px_4px]" />
      </div>
    );
  }
  if (type === "pie") {
    return (
      <div className="h-[var(--h)] flex items-center justify-center" style={heightVar}>
        <div className="w-[230px] h-[230px] relative">
          <Shimmer className="w-full h-full rounded-full" />
          <div className="absolute top-[21.5%] left-[21.5%] w-[57%] h-[57%] bg-white rounded-full" />
        </div>
      </div>
    );
  }
  if (type === "table") {
    return (
      <div className="py-1">
        {[...Array(7)].map((_, i) => (
          <Shimmer key={i} className="h-[18px] mb-2.5 rounded-[3px]" />
        ))}
      </div>
    );
  }
  if (type === "heatmap") {
    return (
      <div className="py-2 h-[var(--h)] flex flex-col justify-between" style={heightVar}>
        {[...Array(7)].map((_, r) => (
          <div key={r} className="grid grid-cols-[120px_repeat(24,1fr)] gap-0.5 flex-1 max-h-8">
            <Shimmer className="h-full" />
            {[...Array(24)].map((_, c) => (
              <Shimmer key={c} className="h-full" />
            ))}
          </div>
        ))}
      </div>
    );
  }
  return <Shimmer className="h-[var(--h)]" style={heightVar} />;
}

export function MetricLabel({ title, hint }) {
  return (
    <span className="inline-flex items-center gap-1">
      {title}
      <AntTooltip title={hint}>
        <QuestionCircleOutlined className="text-slate-400 text-[11px] cursor-help" />
      </AntTooltip>
    </span>
  );
}

export function ChartError({ message, onRetry, height = 220 }) {
  return (
    <div className="h-[var(--h)] flex flex-col items-center justify-center gap-2.5" style={{ "--h": `${height}px` }}>
      <span className="text-[12px] text-slate-400">{message || "Failed to load this chart"}</span>
      <Button size="small" onClick={onRetry}>Retry</Button>
    </div>
  );
}

// Loading / error / empty wrapper for a chart body.
export function ChartState({ state, skeleton, height = 280, onRetry, empty, children }) {
  if (state.loading) return <ChartSkeleton type={skeleton} height={height} />;
  if (state.error) return <ChartError message={state.error} onRetry={onRetry} height={height} />;
  if (empty) return <Empty description="No data" />;
  return children;
}
